import { Box, SxProps, Theme, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  AthletesModel,
  Exercises,
  LiftField,
  RecordModel,
} from "../../../util/src/types";
import {
  formatRecordTitleWithAge,
  isRecordAttempt,
  type RecordAttemptMark,
} from "../../../util/src/record-util";
import dico from "../../../util/src/language/dico";
import FrenchFlag from "./FrenchFlag.js";

/** Bounding box matching the historical French-flag indicator size (grid cells). */
const FLAG_BOX_WIDTH = 16;
const FLAG_BOX_HEIGHT = 8;
/**
 * Extra height allowed for square badges so they stay readable while wide
 * flags still resolve to the classic 16×8 envelope via aspect fit.
 */
const SQUARE_BADGE_MIN_BOX_HEIGHT = 12;

interface RenderRecordProps {
  athlete: AthletesModel;
  lift: LiftField | string;
  total: boolean;
  weight: number;
  records: RecordModel[];
  sx?: SxProps<Theme>;
  blink?: boolean;
  flagWidth?: number;
  flagHeight?: number;
  position?: "left" | "right";
}

const getDefaultSx = (position: "left" | "right"): SxProps<Theme> => ({
  position: "absolute",
  top: 1,
  [position]: 1,
  zIndex: 2,
  lineHeight: 0,
  pointerEvents: "auto",
});

/**
 * Fits an image into a box while preserving its intrinsic aspect ratio.
 *
 * @param naturalWidth - Source pixel width.
 * @param naturalHeight - Source pixel height.
 * @param maxWidth - Bounding box width.
 * @param maxHeight - Bounding box height.
 * @returns Display width/height in CSS pixels.
 */
function fitAspectSize(
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: maxWidth, height: maxHeight };
  }
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
  };
}

/**
 * Builds a multi-line tooltip listing beaten marks (one block per organisation).
 * Header is `Record de France · SNR`; detail line is `poids · athlète`.
 */
function buildRecordTooltip(beaten: RecordAttemptMark[]): string {
  const fallback = dico().admin.records.displayModal.organisationFallback;
  // One mark per org already from isRecordAttempt - keep weight-desc order
  return beaten
    .map((record) => {
      const title = formatRecordTitleWithAge(record, fallback);
      return `${title}\n${record.poids}kg · ${record.athleteName}`;
    })
    .join("\n\n");
}

type RecordBadgeProps = {
  record: RecordAttemptMark;
  flagWidth: number;
  flagHeight: number;
};

/**
 * Single organisation badge (or French flag fallback) sized to the cell box.
 */
const RecordBadge: React.FC<RecordBadgeProps> = ({
  record,
  flagWidth,
  flagHeight,
}) => {
  const [badgeNaturalSize, setBadgeNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // CSP / network failures must not leave a broken <img> icon in the grid
  const [badgeLoadFailed, setBadgeLoadFailed] = useState(false);
  const badgeUrl = record.recordBadgeUrl || null;
  // Reset failure state when the URL changes (sync / org switch)
  useEffect(() => {
    setBadgeLoadFailed(false);
    setBadgeNaturalSize(null);
  }, [badgeUrl]);
  const badgeAlt =
    record.recordLabel ||
    record.organisationName ||
    record.athleteName ||
    "record";
  const badgeBoxWidth = flagWidth;
  const badgeBoxHeight =
    flagHeight <= FLAG_BOX_HEIGHT
      ? Math.max(flagHeight, SQUARE_BADGE_MIN_BOX_HEIGHT)
      : flagHeight;
  const badgeDisplaySize = badgeNaturalSize
    ? fitAspectSize(
        badgeNaturalSize.width,
        badgeNaturalSize.height,
        badgeBoxWidth,
        badgeBoxHeight,
      )
    : { width: flagWidth, height: flagHeight };
  if (!badgeUrl || badgeLoadFailed) {
    return (
      <FrenchFlag width={flagWidth} height={flagHeight} top={0} right={0} />
    );
  }
  return (
    <Box
      component="img"
      src={badgeUrl}
      alt={badgeAlt}
      onLoad={(event) => {
        const img = event.currentTarget;
        setBadgeNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }}
      onError={() => setBadgeLoadFailed(true)}
      sx={{
        backgroundColor: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: "1px",
        boxShadow: "0 0 1px rgba(0,0,0,0.35)",
        display: "block",
        height: badgeDisplaySize.height,
        objectFit: "contain",
        width: badgeDisplaySize.width,
      }}
    />
  );
};

/**
 * Shows a record-break indicator when the attempt exceeds matching marks.
 * Only the highest beaten mark is shown as a badge; the tooltip lists every
 * organisation level beaten (league / national / world).
 */
const RenderRecord: React.FC<RenderRecordProps> = ({
  athlete,
  lift,
  total,
  weight,
  records,
  sx,
  blink = false,
  flagWidth = FLAG_BOX_WIDTH,
  flagHeight = FLAG_BOX_HEIGHT,
  position = "right",
}) => {
  const exercise = total
    ? "Total"
    : Exercises.find((e) => e.lifts.includes(lift as LiftField));
  const attempt =
    exercise === undefined
      ? null
      : isRecordAttempt(weight, athlete, records, exercise);
  const highestBadge = attempt?.beatenRecord ?? null;
  const beatenRecords = attempt?.beatenRecords ?? [];
  if (!weight || !highestBadge) {
    return null;
  }
  const baseSx = {
    ...getDefaultSx(position),
    ...sx,
  };
  // Tooltip keeps the full multi-org breakdown even though only one logo shows
  const tooltip = buildRecordTooltip(beatenRecords);
  return (
    <>
      {blink && (
        <style>{`
                @keyframes blink {
                    0% { opacity: 1; }
                    50% { opacity: 0.3; }
                    100% { opacity: 1; }
                }
            `}</style>
      )}
      <Tooltip
        title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltip}</Box>}
        enterTouchDelay={0}
        leaveTouchDelay={50000}
      >
        <Box
          sx={{
            ...baseSx,
            display: "flex",
            flexDirection: "column",
            alignItems: position === "right" ? "flex-end" : "flex-start",
            ...(blink && {
              animation: "blink 2s infinite",
            }),
          }}
        >
          <RecordBadge
            record={highestBadge}
            flagWidth={flagWidth}
            flagHeight={flagHeight}
          />
        </Box>
      </Tooltip>
    </>
  );
};

export default RenderRecord;
